<template>
  <!--
    「요금·결제」의 체험 기간 정하기 창(설계안 §24 → §25.4 — 결제 안 된 구독까지). 상태마다 말만 다르다: 시작 전 「체험 시작」(지금부터 고른
    끝까지) · 체험 중 「체험 기간 바꾸기」(지금 끝내기 = `trialEndDtm: null` 서버 시계 / 날짜·시각 — 앞당기기·늘리기) · 유예·멈춤 「다시
    체험」(지금부터 고른 끝까지, 처음 시작한 날은 그대로). 사유는 필수(§25.10 Q1). 끝난 뒤의 일은 서버의 기존 규칙 그대로 — 이 창은
    정하기만 한다. 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(view.title)"
    :visible="visible"
    width="460px"
    append-to-body
    data-testid="subscription-trial-end-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <p class="subscription-trial-end__store">{{ storeLabel }}</p>
    <p v-if="currentPeriod" class="subscription-trial-end__current" data-testid="subscription-trial-end-current">
      {{ $t(text.trialEndCurrent) }} {{ currentPeriod }}
    </p>
    <el-form label-position="top" @submit.native.prevent>
      <el-form-item :label="$t(text.trialEndNew)" :error="problems.picked">
        <el-radio-group
          v-if="view.canEndNow"
          v-model="form.mode"
          data-testid="subscription-trial-end-mode"
          @change="problems.picked = ''">
          <el-radio :label="MODE.NOW">{{ $t(text.trialEndNow) }}</el-radio>
          <el-radio :label="MODE.PICK">{{ $t(text.pickMoment) }}</el-radio>
        </el-radio-group>
        <SubscriptionMomentPicker
          v-if="form.mode === MODE.PICK"
          v-model="form.picked"
          :class="{ 'subscription-trial-end__picker': view.canEndNow }"
          data-testid="subscription-trial-end-picker"
          @change="problems.picked = ''" />
      </el-form-item>
      <el-form-item :label="$t(text.reason)" :error="problems.reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="2"
          data-testid="subscription-trial-end-reason"
          :placeholder="$t(text.reasonPlaceholder)"></el-input>
      </el-form-item>
    </el-form>
    <p class="subscription-trial-end__notice" data-testid="subscription-trial-end-notice">{{ $t(notice) }}</p>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.close) }}</el-button>
      <el-button
        size="small"
        type="primary"
        data-testid="subscription-trial-end-submit"
        :loading="busy"
        @click="submit"
        >{{ $t(text.trialEndSubmit) }}</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import SubscriptionMomentPicker from '@/components/subscription/SubscriptionMomentPicker.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  TRIAL_END_MODE,
  trialPeriodText,
  trialSetKindOf,
  trialSetNoticeOf,
  trialSetRequestOf,
  trialSetViewOf,
} from '@/utils/subscriptionAdmin'

const noProblems = () => ({ picked: '', reason: '' })

export default {
  name: 'SubscriptionTrialEndDialog',

  components: { SubscriptionMomentPicker },

  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
    /** 요청할 구독 — 상세 창이 가진 ID(상세를 다시 읽는 동안·읽기 실패에도 그대로다) */
    subscriptionId: { type: Number, default: null },
    /** 상세(`GET /api/admin/subscriptions/{id}`) — 그리기만 한다. 다시 읽는 동안은 `null` */
    detail: { type: Object, default: null },
    /** 매장 이름(「QA 피자 · S2600…」) */
    storeLabel: { type: String, default: '' },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      MODE: TRIAL_END_MODE,
      /** 연 때의 갈래 — 409 뒤 상세를 다시 읽는 동안에도 창의 말이 바뀌지 않게 열 때 정한다 */
      kind: trialSetKindOf(null),
      form: { mode: TRIAL_END_MODE.PICK, picked: '', reason: '' },
      problems: noProblems(),
      busy: false,
    }
  },

  computed: {
    view: function () {
      return trialSetViewOf(this.kind)
    },
    currentPeriod: function () {
      return trialPeriodText(this.detail)
    },
    notice: function () {
      return trialSetNoticeOf(this.detail, this.kind)
    },
  },

  methods: {
    /** 열 때마다 새로 — 상태로 갈래를 정하고(체험 중이면 「지금 끝내기」가 처음 선택), 고른 값·사유·문장을 비운다 */
    reset: function () {
      this.kind = trialSetKindOf(this.detail)
      this.form = { mode: this.view.canEndNow ? TRIAL_END_MODE.NOW : TRIAL_END_MODE.PICK, picked: '', reason: '' }
      this.problems = noProblems()
      this.busy = false
    },

    /**
     * 정하기 — 보내고, 말하고, 부모가 상세·목록을 다시 읽게 한다. 거절(400 지난 시각·체험 시작 앞 · 409 `TRIAL_UNAVAILABLE`·
     * `CHARGE_PENDING`·`STATUS_CHANGED`)은 서버 문장을 창 안에서 한 번 말한다 — 409 면 그 사이 바뀐 것이라 창을 열어 둔 채 다시 읽는다.
     */
    submit: async function () {
      const request = trialSetRequestOf(this.subscriptionId, this.form, Date.now())
      this.problems = noProblems()
      if (!request.ok) {
        this.problems = Object.assign(noProblems(), { [request.field]: this.$t(request.problem) })
        return
      }

      this.busy = true
      try {
        await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)

        this.$emit('update:visible', false)
        this.mainVue.$message({ type: 'success', message: this.$t(this.view.done) })
        this.$emit('changed')
      } catch (error) {
        this.problems.picked = httpErrorMessage(error, this.$t(this.text.writeFailed))
        if (error && error.status === 409) this.$emit('changed')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-trial-end__store,
.subscription-trial-end__current {
  margin: 0 0 8px;
  color: var(--color-text-regular);
}

.subscription-trial-end__picker {
  margin-top: 8px;
}

.subscription-trial-end__notice {
  margin: 0;
  color: var(--color-text-secondary);
  word-break: keep-all;
}
</style>
